import api from '@shared/services/api';
import { TutorialBankItem, TutorialBankProgress, TutorialBankTestAnswer } from '../types/tutorial-bank.types';

export class TutorialBankService {
  /**
   * Get all tutorials
   * @param page Page number (1-indexed)
   * @param limit Items per page
   * @param categoryId Optional category ID filter
   * @returns Paginated tutorial items
   */
  static async getTutorials(page: number = 1, limit: number = 10, categoryId?: string) {
    const response = await api.get('/tutorial-bank/items', {
      params: {
        page,
        limit,
        categoryId
      }
    });
    return response.data;
  }

  /**
   * Get all published tutorials for a specific category
   * @param categoryId The category ID to filter by
   * @returns Array of tutorial bank items
   */
  static async getTutorialsByCategory(categoryId: string): Promise<TutorialBankItem[]> {
    const response = await api.get(`/tutorial-bank/category/${categoryId}/tutorials`);
    return response.data.tutorials;
  }

  /**
   * Get a single tutorial by ID
   * @param tutorialId The tutorial ID
   * @returns Tutorial bank item
   */
  static async getTutorialById(tutorialId: string): Promise<TutorialBankItem> {
    const response = await api.get(`/tutorial-bank/items/${tutorialId}`);
    return response.data.item;
  }

  /**
   * Get the user's progress for a specific tutorial
   * @param tutorialId The tutorial ID
   * @returns User progress or null if not found
   */
  static async getUserProgress(tutorialId: string): Promise<TutorialBankProgress | null> {
    try {
      const response = await api.get(`/tutorial-bank/progress/${tutorialId}`);
      return response.data.progress;
    } catch (error) {
      // Return null if no progress found (404 error)
      if (error.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Start a new tutorial session or update existing session
   * @param tutorialId The tutorial ID
   * @param categoryId The category ID
   * @returns Progress object
   */
  static async startOrUpdateTutorialSession(
    tutorialId: string,
    categoryId: string
  ): Promise<TutorialBankProgress> {
    const response = await api.post('/tutorial-bank/progress/start', {
      tutorialId,
      categoryId
    });
    return response.data.progress;
  }

  /**
   * Save a user's answer to a question
   * @param progressId The progress session ID
   * @param answer The answer details
   * @returns Updated progress object
   */
  static async saveAnswer(
    progressId: string,
    answer: TutorialBankTestAnswer
  ): Promise<TutorialBankProgress> {
    const response = await api.post(`/tutorial-bank/progress/${progressId}/answer`, answer);
    return response.data.progress;
  }

  /**
   * Mark a tutorial as completed
   * @param progressId The progress session ID
   * @returns Updated progress object
   */
  static async completeTutorial(progressId: string): Promise<TutorialBankProgress> {
    const response = await api.post(`/tutorial-bank/progress/${progressId}/complete`);
    return response.data.progress;
  }
}
